import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";

export default function SetupPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const [userInfo, setUserInfo] = useState(null);   // { email, name }
  const [tokenError, setTokenError] = useState("");  // invalid token msg
  const [loading, setLoading] = useState(true);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Nieuwe link aanvragen state
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendSending, setResendSending] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  // Valideer token bij laden
  useEffect(() => {
    if (!token) { setTokenError("Geen token gevonden."); setLoading(false); return; }
    fetch(`${API}/api/auth/validate-setup-token/${token}`)
      .then(async r => {
        const text = await r.text();
        let d = {};
        try { d = JSON.parse(text); } catch {}
        if (!r.ok) throw new Error(d.detail || "Ongeldige of verlopen activatielink.");
        return d;
      })
      .then(data => { setUserInfo(data); setLoading(false); })
      .catch(e => {
        const msg = e.message || "";
        const isUserReadable = msg && !msg.includes("disturbed") && !msg.includes("locked")
          && !msg.includes("fetch") && !msg.includes("JSON") && !msg.includes("execute")
          && !msg.includes("stream") && !msg.includes("body") && msg.length < 120;
        setTokenError(isUserReadable ? msg : "Ongeldige of verlopen activatielink.");
        setLoading(false);
      });
  }, [token]);

  const handleResendLink = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim() || resendSending) return;
    setResendSending(true);
    try {
      await fetch(`${API}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resendEmail.trim().toLowerCase() }),
      });
      setResendSent(true);
    } catch {}
    setResendSending(false);
  };

  const passwordStrength = () => {
    if (password.length === 0) return null;
    if (password.length < 8) return "te kort";
    if (/(?=.*[A-Z])(?=.*[0-9])/.test(password)) return "sterk";
    if (password.length >= 10) return "goed";
    return "matig";
  };
  const strength = passwordStrength();
  const strengthColor = { sterk: "#4ade80", goed: "#C9A84C", matig: "#f87171", "te kort": "#f87171" };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Wachtwoorden komen niet overeen"); return; }
    if (password.length < 8) { setError("Wachtwoord moet minimaal 8 tekens bevatten"); return; }
    setSubmitting(true); setError("");
    try {
      const r = await fetch(`${API}/api/auth/setup-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password, confirm_password: confirm })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Er is iets misgegaan");
      // Auth context bijwerken én localStorage zetten
      loginWithToken(data.user, data.token);
      setSuccess(true);
      setTimeout(() => navigate("/account"), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-zinc-500 text-sm">Link controleren...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center px-4 py-12">
      {/* Achtergrond glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#C9A84C]/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <img src={LOGO_URL} alt="FitSmith" className="h-9 w-9 object-contain" />
          <span className="text-white font-bold text-lg tracking-wider">
            FitSmith <span style={{ color: GOLD }}>Academy</span>
          </span>
        </div>

        {/* Token error */}
        {tokenError ? (
          <div className="bg-[#111] border border-red-500/30 rounded-2xl p-8 text-center">
            <AlertCircle size={32} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-white font-bold text-lg mb-2">Link ongeldig</h2>
            <p className="text-zinc-400 text-sm mb-6">{tokenError}</p>

            {!showResendForm && !resendSent && (
              <div className="space-y-3">
                <button onClick={() => setShowResendForm(true)}
                  data-testid="resend-link-btn"
                  className="w-full py-3 rounded-xl text-sm font-bold text-black transition-all hover:brightness-110"
                  style={{ background: GOLD }}>
                  Stuur mij een nieuwe activatielink
                </button>
                <button onClick={() => navigate("/login")}
                  className="w-full text-sm font-semibold px-5 py-2.5 rounded-xl border border-white/10 text-zinc-300 hover:text-white hover:border-white/20 transition-colors">
                  Terug naar inloggen
                </button>
              </div>
            )}

            {showResendForm && !resendSent && (
              <form onSubmit={handleResendLink} className="space-y-3 text-left mt-2">
                <label className="block text-zinc-400 text-sm mb-1">Jouw e-mailadres</label>
                <input type="email" value={resendEmail} onChange={e => setResendEmail(e.target.value)}
                  placeholder="jouw@email.nl"
                  data-testid="resend-email-input"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/40" />
                <button type="submit" disabled={!resendEmail.trim() || resendSending}
                  data-testid="resend-email-submit"
                  className="w-full py-3 rounded-xl text-sm font-bold text-black disabled:opacity-40 transition-all"
                  style={{ background: GOLD }}>
                  {resendSending ? "Versturen..." : "Nieuwe link versturen"}
                </button>
                <button type="button" onClick={() => navigate("/login")}
                  className="w-full text-sm text-zinc-500 hover:text-zinc-300 transition-colors py-1">
                  Terug naar inloggen
                </button>
              </form>
            )}

            {resendSent && (
              <div className="mt-2">
                <p className="text-green-400 text-sm mb-4">
                  Als dit e-mailadres bekend is, ontvang je een nieuwe activatielink.
                </p>
                <button onClick={() => navigate("/login")}
                  className="text-sm font-semibold px-5 py-2.5 rounded-xl border border-white/10 text-zinc-300 hover:text-white hover:border-white/20 transition-colors">
                  Terug naar inloggen
                </button>
              </div>
            )}
          </div>
        ) : success ? (
          /* Succes state */
          <div className="bg-[#111] border border-white/8 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: "rgba(74,222,128,0.1)", border: "2px solid rgba(74,222,128,0.3)" }}>
              <CheckCircle size={28} className="text-green-400" />
            </div>
            <h2 className="text-white font-bold text-xl mb-2">Wachtwoord ingesteld!</h2>
            <p className="text-zinc-400 text-sm">Je wordt automatisch doorgestuurd naar je account...</p>
          </div>
        ) : (
          /* Setup formulier */
          <div className="bg-[#111] border border-white/8 rounded-2xl p-8">
            <div className="text-center mb-7">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: GOLD }}>
                Account activeren
              </p>
              <h1 className="text-white font-bold text-2xl mb-1">
                Welkom{userInfo?.name ? `, ${userInfo.name.split(" ")[0]}` : ""}!
              </h1>
              <p className="text-zinc-500 text-sm">
                Stel je persoonlijke wachtwoord in
                {userInfo?.email && <><br /><span className="text-zinc-600">{userInfo.email}</span></>}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3" data-testid="setup-password-form">
              {/* Nieuw wachtwoord */}
              <div>
                <label className="course-label">Nieuw wachtwoord</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <input
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError(""); }}
                    placeholder="Minimaal 8 tekens"
                    data-testid="setup-password-input"
                    className="course-input pl-9 pr-10"
                    autoFocus
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                    {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {strength && (
                  <p className="text-xs mt-1.5 font-semibold" style={{ color: strengthColor[strength] }}>
                    Sterkte: {strength}
                  </p>
                )}
              </div>

              {/* Bevestig wachtwoord */}
              <div>
                <label className="course-label">Bevestig wachtwoord</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={e => { setConfirm(e.target.value); setError(""); }}
                    placeholder="Herhaal je wachtwoord"
                    data-testid="setup-confirm-input"
                    className="course-input pl-9 pr-10"
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                    {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {confirm && password !== confirm && (
                  <p className="text-xs mt-1.5 text-red-400">Wachtwoorden komen niet overeen</p>
                )}
                {confirm && password === confirm && confirm.length >= 8 && (
                  <p className="text-xs mt-1.5 text-green-400">Wachtwoorden komen overeen</p>
                )}
              </div>

              {error && (
                <div data-testid="setup-error" className="bg-red-950/40 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || password.length < 8 || password !== confirm}
                data-testid="setup-submit-btn"
                className="btn-gold-course w-full py-3.5 font-bold text-sm uppercase tracking-wide mt-2 disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {submitting ? "Bezig..." : <><span>Wachtwoord instellen</span> <ArrowRight size={14} /></>}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
